import React, { ReactNode, useState } from 'react';
import { BlogCard as Card } from '../../components/BlogCard';
import { CardType } from '../../components/CardContent/logic';
import { CardContent } from '../../components/CardContent';
import { AsComponentProps } from '../../types/module';
import { classNames } from '../../utils/style';
import { ImageProps } from '../../components/Image/types';
import { TagBlockProps } from '../../components/Tag';

export interface BlogCardLayoutProps {
  linkAs?: AsComponentProps;
  title: string;
  subtitle: string;
  hasSubtitleHover?: boolean;
  displaySubtitleOnSmallDevice?: boolean;
  tags?: TagBlockProps[];
  capsText?: string;
  logo?: ImageProps;
  image?: ImageProps;
  imgPosition?: 'top' | 'bottom' | 'right' | 'left' | 'none';
  ctaButton?: ReactNode;
  hasTransparentBorders?: boolean;
  className?: string;
  onCardClick?: () => void;
  linkProps?: any;
  as?: 'div' | 'article' | 'section';
  TitleAs?: 'h2' | 'h3' | 'div';
  SubtitleAs?: 'p' | 'div';
  disableCursorPointer?: boolean;
  cardType?: 'BlogCard' | 'InspirationalCard';
}

export const BlogCardLayout = ({
  linkAs,
  title,
  subtitle,
  hasSubtitleHover = false,
  displaySubtitleOnSmallDevice = false,
  tags,
  capsText,
  logo,
  image,
  imgPosition,
  ctaButton,
  className,
  hasTransparentBorders = false,
  onCardClick,
  linkProps,
  cardType = 'BlogCard',
  as = 'article',
  TitleAs = 'h3',
  SubtitleAs = 'p',
  ...props
}: BlogCardLayoutProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      as={linkAs}
      hasSubtitleHover={hasSubtitleHover}
      displaySubtitleOnSmallDevice={displaySubtitleOnSmallDevice}
      imgPosition={imgPosition}
      hasTransparentBorders={hasTransparentBorders}
      onCardClick={onCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={classNames('BlogCard', className)}
      {...linkProps}
      {...props}
    >
      <Card.Type as={as}>
        {image && ['top', 'bottom'].includes(imgPosition || '') && (
          <Card.Image {...image} />
        )}
        <Card.Content>
          <CardContent
            className="z-20"
            cardType={CardType?.[cardType]}
            hasSubtitleHover={hasSubtitleHover}
            displaySubtitleOnSmallDevice={displaySubtitleOnSmallDevice}
            isHovered={isHovered}
          >
            <div className="max-h-[90%] overflow-hidden">
              {capsText && <CardContent.Caps>{capsText}</CardContent.Caps>}
              {!!tags?.length && (
                <CardContent.Tags
                  tags={tags.map((tag) => ({ ...tag, title: tag?.title || '' }))}
                />
              )}
              {logo && <CardContent.Logo {...logo} className={'h-32'} />}

              <CardContent.Title as={TitleAs}>{title}</CardContent.Title>
              <CardContent.Subtitle as={SubtitleAs}>
                {subtitle}
              </CardContent.Subtitle>
            </div>
            {ctaButton && <CardContent.Cta>{ctaButton}</CardContent.Cta>}
          </CardContent>
        </Card.Content>
        {image && ['right', 'left'].includes(imgPosition || '') && (
          <Card.Image {...image} />
        )}
      </Card.Type>
    </Card>
  );
};